import { ClientEvents } from 'common/events/ClientEvents';
import { MEAL_TYPE_CONTAINER_ID, MealType } from 'common/MealTypeUtils';
import { forwardRef, useCallback, useEffect } from 'react';
import { useMenuBarReducer } from 'src/pages/consumer/components/DishFirstHeader/store';
import { StarV2 } from 'src/shared/design-system/Icon';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useTracker } from 'src/shared/hooks/useTracker';
import { setNavigationContent } from 'src/store/navbar/utils';
import { useMealTypeMenuBarContext } from '../../../context';
import { getMealTypeId, getMealTypeTitle } from '../../../utils';
import { FilterCard } from '../FilterCard';
import { FilterCardRow } from '../FilterCardRow';
import { MEAL_TYPE_TO_ICON } from './icons';
import { Container, Header, MenuBarContainer } from './styles';
import { Tab } from './Tab';
import { BrowseByMealTypeProps } from './types';

export const BrowseByMealType = forwardRef<HTMLDivElement, BrowseByMealTypeProps>((props, ref): JSX.Element => {
  const { segmentFilters, segments } = props;
  const menuBar = useMenuBarReducer();
  const { availableMealTypes, activeMealType, setActiveMealType } = useMealTypeMenuBarContext();
  const tracker = useTracker();
  const history = useHistory();

  const handleClickMealType = useCallback(
    (mealType: MealType, position: number, location?: string) => {
      const currentMealType = window.document.getElementById(getMealTypeId(mealType));
      currentMealType?.scrollIntoView({ behavior: 'smooth' });
      setActiveMealType(mealType);
      tracker.track(ClientEvents.BROWSE_BY_MEAL_TYPE_CLICK, {
        location: location ?? '',
        mealType,
        position,
        segmentFilters,
        pattern: history.location.pathname,
      });
    },
    [setActiveMealType]
  );

  const filters = availableMealTypes.map((mealType, position) => (
    <FilterCard
      key={mealType}
      icon={MEAL_TYPE_TO_ICON[mealType] ?? <StarV2 />}
      label={getMealTypeTitle(mealType)}
      onClick={() => {
        handleClickMealType(mealType, position + 1, 'carousel');
      }}
    />
  ));

  useEffect(() => {
    if (menuBar.isShowing && availableMealTypes.length < 2) {
      menuBar.hide();
    }
  }, [menuBar, availableMealTypes]);

  return (
    <>
      {setNavigationContent(
        <>
          <Container ref={ref}>
            <Header>Browse By Meal Type</Header>
          </Container>
          <FilterCardRow data={segments}>{filters}</FilterCardRow>
        </>,
        MEAL_TYPE_CONTAINER_ID
      )}
      {availableMealTypes.length > 1 &&
        menuBar.setContent(
          <MenuBarContainer>
            {availableMealTypes.map((mealType, position) => (
              <Tab
                key={mealType}
                label={getMealTypeTitle(mealType)}
                isActive={mealType === activeMealType}
                onClick={() => {
                  handleClickMealType(mealType, position + 1, 'menu-bar');
                }}
              />
            ))}
          </MenuBarContainer>
        )}
    </>
  );
});
